<template>
	<div class="bee-live-preview" :style="{ height }">
		<v-card
			flat
			outlined
			height="100%"
			rounded
			class="pa-1"
		>
			<canvas ref="barcode" id="barcode" class="d-none"/>
			<div>
				<canvas
					v-for="i in barcodeCount"
					ref="barcodeG"
					:id="`barcode${i}`"
					:key="i"
				/>
			</div>
			<iframe
				id="life-preview-frame"
				ref="lifePreviewFrame"
				frameborder="0"
				width="100%"
				height="100%"
			/>

			<div id="print-area" ref="printArea"></div>
		</v-card>

		<!-- zoom control -->
		<div class="text-center">
			<div class="d-inline-flex align-center mt-2">
				<v-btn
					v-if="showPrint"
					depressed
					color="primary"
					small
					:disabled="previewSize === 50"
					min-width="0"
					width="24"
					height="24"
					class="rounded-sm pa-0 me-4"
					@click="printPreview"
				>
					<v-icon size="22">$printer</v-icon>
				</v-btn>
				<!-- <v-btn
					v-if="showPrint"
					depressed
					color="primary"
					small
					:disabled="previewSize === 50"
					min-width="0"
					width="24"
					height="24"
					class="rounded-sm pa-0 me-4"
					@click="getContentPagePosition"
				>
					<v-icon size="22">mdi-calculator</v-icon>
				</v-btn> -->

				<v-btn
					depressed
					color="primary"
					small
					:disabled="previewSize === 100"
					min-width="0"
					width="24"
					height="24"
					class="rounded-sm pa-0"
					@click="previewSize < 100 ? previewSize+= 10 : null"
				>
					<v-icon size="22">$add</v-icon>
				</v-btn>
				<div class="text-center flex-grow-1 mx-3">{{previewSize}}%</div>
				<v-btn
					depressed
					color="primary"
					small
					:disabled="previewSize === 50"
					min-width="0"
					width="24"
					height="24"
					class="rounded-sm pa-0"
					@click="previewSize > 50 ? previewSize-= 10 : null"
				>
					<v-icon size="22">$remove</v-icon>
				</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
import { questionType, subContentType } from '@/helpers/enums';
import printJS from 'print-js';
import { convertFromCmToPx, generateBarcode, generateBarcodeString } from '@/helpers/functions';
import { hideExamBarcode, hideExamMarkers } from '@/configs/general.configs'
export default {
	name: 'BeeLivePreview',

	props: {
		height: { type: String, default: '90%' },
		barcodeText: { type: String, default: '.' },
		questions: { type: Array, default: () => [] },
		showPrint: { type: Boolean, default: false },
		showPageData: { type: Boolean, default: false },
	},

	data() {
		return {
			previewSize: 70,
			barcodeCount: 0,
			printIframe: null,
		}
	},

	watch: {
		barcodeText: {
			handler(val) {
				this.$nextTick(() => {
					if (val) {
						generateBarcode(this.$refs.barcode, val);
						this.previewSize = 60;
					}
				})
			},
			immediate: true
		},
		livePreviewHTML: {
			handler(val) {
				this.$nextTick(() => {
					if (this.$refs.lifePreviewFrame) {
						this.$refs.lifePreviewFrame.contentWindow.document.body.innerHTML = `
							<div id="app" class="v-application v-application--is-ltr">
								<div class="v-main">
									<div class="v-main__wrap">${val}</div>
								</div>
							</div>
						`;
					}
				})
			},
			immediate: true
		}
	},

	computed: {
		onPrintStyles() {
			/* eslint-disable indent, multiline-ternary */
			return `
				#app { zoom: 60% }
				
				/* footer & header declaration */
				#app .header, .header-space {
					/* header height */
					/* height: fit-content; */
					height: ${!hideExamBarcode ? '7cm' : '5cm'};
				}
				#app .footer, .footer-space {
					/* footer height */
					height: 60px;
				}
				#app .header { position: fixed; top: 0; }
				/* #app .footer { position: fixed; bottom: 0; } */
				#app .footer { position: fixed; left:0; right: 0; bottom: 0; }

				/* layout declaration */
				#app * { font-family: Tahoma, Geneva, Verdana, sans-serif !important; }
				#app #header * { font-size: 16pt !important; }
				#app #header { padding-left: calc(2cm + 20px); }
				#app #questions-container { padding-left: 2cm; }
				#app #questions-container { padding-right: 5cm; }
				#app #questions-container * { font-size: 22pt !important; }

				/* logo */
				#app #header #logo {
					/* need fixed for keep logo position in header */
					position: absolute;
					top: 0;
					left: calc(2cm + 20px);
					/* left: 0; */
					/* transform: translateX(-50%); */
				}

				/* question layout */
				#app .question { padding-left: 20px; }
				#app .question:not(.question-title) { margin-bottom: 1cm !important; }
				#app .question-title {
					font-weight: 700;
					line-height: 1.2cm;
				}
				
				/* marker declaration */
				${hideExamMarkers ? '' : `
				#app .question:not(.question-title)::before, #app .answer::before {
					font-family: "icomoon";
					position: absolute;
					left: 0;
					transform: translateY(2pt);
					font-weight: 700;
					font-size: 20pt;
				}
				#app .question:not(.question-title)::before { content: '\\e901'; }
				#app .answer::before { content: '\\e900'; }
				`}

				/* image */
				#app img:not(#header img, .custom-image) { width: 6cm; }
			`
			/* eslint-enable indent, multiline-ternary */
		},

		previewStyle() {
			const styles = `
				#app #preview-table { width: 100%; }

				/* global declarations */
				#app { zoom: ${this.previewSize}% }
				#app p { margin: 0; }
				#app .question p:first-child { margin: 0; display: inline-block; }
				#app img:not(#header img, .custom-image) { width: 130px; }
				#app img { max-width: 100%; max-height: fit-content; }
				#app ol[type="A"] {
					padding-left: 1.5em !important;
				}
			`;

			return `
				<style>
					/* custom styles */
					${styles}

					/* print declarations */
					${!this.showPageData ? `@media print { ${this.onPrintStyles} }` : this.onPrintStyles}
					
					@page {
						size: A4;
						margin-left: 0.8cm;
						
						@bottom-right {
							content: "Page " counter(page) " of " counter(pages);
							font-size: 12px;
							color: #333;
						}
					}
				</style>
			`
		},

		livePreviewHTML() {
			let html = '';
			let questionCounter = 0;
			html += this.questions.map((q, i) => {
				switch (q.type) {
				case questionType.single: return this.generateQuestionHtml(q, ++questionCounter);
				case questionType.group: {
					const subContentHtml = q.subContent.map((c, subIndex) => {
						switch (c.type) {
						case subContentType.question: return this.generateQuestionHtml(c, `${questionCounter + 1}.${subIndex + 1}`);
						case subContentType.divider: return '<hr class="my-2 track-page">';
						case subContentType.text: return `<p class="my-2 track-page text-pre-line">${c.text || ''}</p>`;
						case subContentType.image: return `
							<div class="my-2 track-page ${c.imageOptions.align ? 'text-' + c.imageOptions.align : ''}">
								<img src="${c.preview}" width="${c.imageOptions.width || 130}px" height="${c.imageOptions.height || null}" class="custom-image rounded">
							</div>
						`;
						default: return '';
						}
					})
					return `
						<h2 class="question question-title text-body-1 track-page">${++questionCounter}. ${q.text}</h2>
						${q.image ? `<img src="${this.generateImageUrl(q.image)}" class="d-block my-2 rounded">` : ''}
						<div class="ms-0 mt-2">${subContentHtml.join('')}</div>
					`
				};
				default: 
					switch (q.contentType) {
					case subContentType.divider: return '<hr class="my-2 track-page">';
					case subContentType.text: return `<p class="my-2 track-page text-pre-line">${q.text || ''}</p>`;
					case subContentType.image: return `
						<div class="my-2 track-page ${q.imageOptions.align ? 'text-' + q.imageOptions.align : ''}">
							<img src="${q.preview}" width="${q.imageOptions.width || 130}px" height="${q.imageOptions.height || null}" class="custom-image rounded">
						</div>
					`;
					default: return '';
					};
				}
			}).join('');
			
			return `
				<table id="preview-table">
					<thead><tr><td><div class="header-space">
						<div
							id="header"
							class="${!this.showPageData ? 'd-none d-print-flex' : 'd-flex'} flex-column mb-6"
						>
							<div class="d-flex justify-center align-center mb-4">
								<img id="logo" src="${require('@/assets/school-logo.png')}" height="60">
								${!hideExamBarcode && this.$refs.barcode ? `<img src="${this.$refs.barcode?.toDataURL()}" height="60" style="position: relative; top: 5cm; margin-left: -2cm;">` : '<div style="height: 60px"></div>'}
							</div>

							<div class="d-flex flex-grow-1 align-center">
								<div class="d-flex flex-column flex-grow-1">
									<span>اسم الطالب</span>
									<span>Student name</span>
								</div>
								
								<hr class="v-divider v-divider--vertical v-divider--inset theme--light mx-6">

								<div class="d-flex flex-column flex-grow-1">
									<span>Exam Date</span>
									<span>Exam name</span>
								</div>
								
								<hr class="v-divider v-divider--vertical v-divider--inset theme--light mx-6">
								
								<p class="mb-0 flex-grow-1">Mark</p>
							</div>
						</div>
					</div></td></tr></thead>

					<tbody><tr><td><div class="content">
						<div id="questions-container">
							${html}
							${this.previewStyle}
						</div>
					</div></td></tr></tbody>

					<tfoot><tr><td><div class="footer-space">
						<!-- spacer -->
					</div></td></tr></tfoot>
				</table>

				<!-- footer -->
				<div class="footer justify-center align-end"></div>
			`;
		},
	},

	methods: {
		generateImageUrl(image) {
			if (image) {
				return typeof image === 'string' ? image : URL.createObjectURL(image);
			} else {
				return null;
			}
		},

		generateQuestionHtml(question, index = 0) {
			const optionsHtml = question.options.map((c, i) => `
				<li class="answer">
					<div class="d-flex align-center">
						<i class="v-icon me-1 mdi mdi-checkbox-blank-circle-outline theme--light"></i>
						<span>${c.text}</span>
					</div>
				</li>
			`);
			return `
				<div class="question mb-4 track-page">
					<h2 class="question-title text-body-1 mb-1">${index}. ${question.text}</h2>
					<div class="d-flex justify-space-between align-center">
						<ol type="A" class="pl-2">${optionsHtml.join('')}</ol>
						${question.image ? `<img src="${this.generateImageUrl(question.image)}" width="110" class="rounded ml-4">` : ''}
					</div>
				</div>
			`;
		},

		printPreview() {
			printJS({
				printable: this.$refs.lifePreviewFrame.contentWindow.document.documentElement.innerHTML,
				type: 'raw-html',
			})
		},

		getContentPagePosition() {
			this.printIframe = document.createElement('iframe');
			this.printIframe.width = '100%';
			this.printIframe.height = '100%';

			console.clear();
			
			const calculatePages = () => {
				const iframeDocument = this.printIframe.contentWindow.document;

				let prevPageNum = 0;
				let pageOffset = 0;
				iframeDocument.querySelectorAll('.track-page').forEach(el => {
					const elementStyles = window.getComputedStyle(el);
					const topAndHeight =
						pageOffset +
						el.offsetTop +
						el.clientHeight +
						+elementStyles.marginTop.slice(0, -2) +
						+elementStyles.marginBottom.slice(0, -2);
					const pageHeight = (
						convertFromCmToPx(29.7, 217) - // A4 height
						2 * convertFromCmToPx(3.81, 217) - // top and bottom page margins
						convertFromCmToPx(7, 217) // header height
					)

					const pageNum = Math.floor(topAndHeight / pageHeight) + 1;

					const actualPosition =
						prevPageNum ? topAndHeight - ((prevPageNum - 1) * pageHeight) : 0;

					if (actualPosition > pageHeight && !el.querySelector('img')) {
						// pageOffset = actualPosition - pageHeight;
						pageOffset = 0;
						el.dataset.page = prevPageNum;
						console.log({ pageOffset });
					} else {
						el.dataset.page = pageNum;
					}

					if (pageNum !== prevPageNum) {
						console.log(el, +pageHeight.toFixed(2));
					}
					
					prevPageNum = pageNum; 
					if (el.classList.contains('question') && !el.classList.contains('question-title')) {
						el.dataset.isQuestion = 1;
					}
					console.log({
						// data: {
						// 	page: el.dataset.page,
						// 	isQuestion: el.dataset.isQuestion,
						// },
						page: +el.dataset.page,
						content: el.innerText.replace(/\n/g, ''),
						actualPosition,
						// pageNum: pageNum,
						// top: el.getBoundingClientRect().top,
						// offsetTop: el.offsetTop,
						// height: el.clientHeight,
						topAndHeight,
						// dddd: +(topAndHeight - ((pageNum - 1) * pageHeight)).toFixed(2),
						distanceFromBottom: +(pageNum * pageHeight - topAndHeight).toFixed(2),
						// pageHeight: +pageHeight.toFixed(2),
					});
				});

				/* Barcode */
				const totalPages = prevPageNum + 1;
				const questionCountTotal = iframeDocument.querySelectorAll('[data-is-question]').length;
				
				// generate barcode
				const barcodes = [];
				let tempQuestionsCount = 0;
				for (let i = 1; i <= totalPages; i++) {
					tempQuestionsCount +=
						iframeDocument.querySelectorAll(`*[data-page="${i}"][data-is-question]`).length
					if (i % 2 === 0) {
						barcodes.push(
							generateBarcodeString({
								questionsCountPerPaper: tempQuestionsCount,
								questionsCountTotal: questionCountTotal,
								paperCount: Math.ceil(i / 2),
								examId: 123,
								subjectId: 456,
								studentId: 789,
							})
						);
						tempQuestionsCount = 0
					}
				}

				/* this.barcodeCount = barcodes.length;
				this.$nextTick(() => {
					for (let i = 0; i < barcodes.length; i++) {
						console.log(barcodes);
						generateBarcode(this.$refs.barcodeG[i], barcodes[i]);
					}
				}) */
				
				// this.printIframe.remove();

				printJS({
					printable: this.printIframe.contentWindow.document.documentElement.innerHTML,
					type: 'raw-html',
				})
			}

			// append iframe to print area
			this.$refs.printArea.innerHTML = '';
			this.$refs.printArea.appendChild(this.printIframe);
			this.printIframe.contentWindow.document.write(
				this.$refs.lifePreviewFrame.contentWindow.document
					.querySelector('html').innerHTML
			);
			this.printIframe.contentWindow.document.body.innerHTML += `
				<style>${this.onPrintStyles}</style>
			`

			// Wait for all images to load
			const images = this.printIframe.contentWindow.document.querySelectorAll('img');
			let loadedImages = 0;
			if (images.length === 0) calculatePages();
			else {
				for (const img of images) {
					if (img.complete) loadedImages++
					else {
						img.onload = img.onerror = () => {
							loadedImages++;
							if (loadedImages === images.length) calculatePages();
						};
					}
				}
			}
		},

		async getPrintPageCount() {
			return new Promise((resolve) => {
				const iframe = document.createElement('iframe');
				iframe.width = '100%';
				iframe.height = '100%';
				
				const calculatePages = () => {
					const pageCount = 
						(
							iframe.contentWindow.document.body.scrollHeight + // content height
							convertFromCmToPx(7, 300) // header height
						) / (
							convertFromCmToPx(29.7) - // A4 height
							2 * convertFromCmToPx(2) // top and bottom page margins
						)
					;
					iframe.remove();
					resolve(pageCount);
				}

				// append iframe to print area
				this.$refs.printArea.appendChild(iframe);
				iframe.contentWindow.document.write(
					this.$refs.lifePreviewFrame.contentWindow.document
						.querySelector('html').innerHTML
				);

				// Wait for all images to load
				const images = iframe.contentWindow.document.querySelectorAll('img');
				let loadedImages = 0;
				if (images.length === 0) calculatePages();
				else {
					for (const img of images) {
						if (img.complete) loadedImages++
						else {
							img.onload = img.onerror = () => {
								loadedImages++;
								if (loadedImages === images.length) calculatePages();
							};
						}
					}
				}
			})
		}
	},

	mounted() {
		// include all style sheets from parent document
		// this.$refs.lifePreviewFrame.contentWindow.document.dir = 'auto';
		document.querySelectorAll('style, link[rel="stylesheet"]').forEach(styleNode => {
			this.$refs.lifePreviewFrame.contentWindow
				.document.head.appendChild(styleNode.cloneNode(true));
		});
	}
}
</script>

<style lang="scss">
.bee-live-preview {
	#print-area {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: -99999;
		visibility: hidden;
	}
	iframe { border: unset; }
}
</style>